import { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useRef } from 'react';
import GuiContext from "config/context/guiContext";
import MemoryContext from '../context/MemoryContext';
import MemoryApiService from '../services/memoryApi';

/**
 * Custom hook for memory operations with optimization and caching
 */
export const useMemory = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetchTime, setLastFetchTime] = useState(null);
    
    const {
        selected: { selectedAccount, selectedServer },
        URL
    } = useContext(GuiContext);
    
    const memoryContext = useContext(MemoryContext);
    
    // Ref for preventing race conditions
    const abortControllerRef = useRef(null);
    
    // Memoized API service instance
    const memoryApi = useMemo(() => new MemoryApiService(URL), [URL]);
    
    // Memoized cache key
    const cacheKey = useMemo(() => 
        `${selectedAccount}-${selectedServer}`, 
        [selectedAccount, selectedServer]
    );
    
    // Memoized current memory data (always called, even if context is null)
    const currentMemoryData = useMemo(() => {
        if (!memoryContext || !memoryContext.memoryData) return null;
        return memoryContext.memoryData[cacheKey] || null;
    }, [memoryContext, cacheKey]);
    
    // getMemoryInfo callback (always called)
    const getMemoryInfo = useCallback(async (forceRefresh = false) => {
        if (!memoryContext) {
            return { success: false, error: 'Context not available' };
        }
        
        const { memoryData, setMemoryData } = memoryContext;
        
        // Cache validation (5 minutes)
        const cacheAge = Date.now() - (lastFetchTime || 0);
        const cacheValid = cacheAge < 5 * 60 * 1000; // 5 minutes
        
        if (!forceRefresh && cacheValid && memoryData[cacheKey]) {
            return { success: true, data: memoryData[cacheKey] };
        }
        
        // Cancel previous request if exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Create new abort controller
        abortControllerRef.current = new AbortController();
        
        setLoading(true);
        setError(null);
        
        try {
            const result = await memoryApi.getMemoryInfo(selectedAccount, selectedServer);
            
            if (result.success) {
                // Update context with new data
                setMemoryData(prev => ({
                    ...prev,
                    [cacheKey]: result.data
                }));
                setLastFetchTime(Date.now());
                return result;
            } else {
                setError(result.error);
                return result;
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                // Request was cancelled, don't set error
                return { success: false, error: 'Request cancelled' };
            }
            
            setError(err.message || 'An error occurred');
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [memoryApi, selectedAccount, selectedServer, cacheKey, lastFetchTime, memoryContext]);
    
    // buyMemory callback (always called)
    const buyMemory = useCallback(async (params) => {
        if (!memoryContext) {
            return { success: false, error: 'Context not available' };
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const result = await memoryApi.buyMemory({
                ...params,
                account_name: selectedAccount,
                server_url: selectedServer
            });
            
            if (result.success) {
                // Refresh memory info after purchase
                await getMemoryInfo(true);
            } else {
                setError(result.error);
            }
            
            return result;
        } catch (err) {
            setError(err.message || 'Failed to buy memory');
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, [memoryApi, selectedAccount, selectedServer, getMemoryInfo, memoryContext]);
    
    // Auto refresh effect (always called)
    //
    // `memoryStamp` je ovde zato što vremenska brana od 5 minuta nije dovoljna:
    // posle brisanja baze ili uvoza broj je netačan odmah, a ne za pet minuta.
    // Kad ga neko poništi (`invalidateMemory`), keš je prazan pa `getMemoryInfo`
    // ne nalazi zapamćen unos i zaista čita sa lanca — vidi MemoryContext.js.
    useEffect(() => {
        if (selectedAccount && selectedServer && memoryContext) {
            getMemoryInfo();
        }

        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [selectedAccount, selectedServer, getMemoryInfo, memoryContext,
        memoryContext?.memoryStamp]);
    
    // Fallback if context is not available
    if (!memoryContext) {
        // console.warn('MemoryContext is not available. Make sure MemoryContextProvider is wrapping the component.');
        return {
            loading: false,
            error: 'MemoryContext not available',
            memoryData: null,
            getMemoryInfo,
            buyMemory,
            // Prazna funkcija, da pozivalac ne mora da proverava postoji li
            // kontekst. Poništavanje keša koji ne postoji je ništa.
            invalidateMemory: () => {},
            lastFetchTime: null,
            cacheKey: cacheKey,
            hasValidCache: false
        };
    }

    return {
        // State
        loading,
        error,
        memoryData: currentMemoryData,

        // Actions
        getMemoryInfo,
        buyMemory,
        invalidateMemory: memoryContext.invalidateMemory,
        
        // Utils
        lastFetchTime,
        cacheKey,
        hasValidCache: lastFetchTime && (Date.now() - lastFetchTime < 5 * 60 * 1000)
    };
}; 